import type { RouteObject } from "react-router-dom";

import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  MyAppMySpaceLayout,
  MyAppMySpaceLibrary,
  MyAppMySpaceCreate,
  MyAppMySpaceAppStore,
  MyAppMySpaceView,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const MS_MY_APP_ID_KEY = "id";

export enum MS_MY_APP_PATH_SEGMENTS {
  // Mirrors the Company surface's URL slug ("my-apps"); permission section
  // stays "my apps" (resolved to the Individual level automatically by path).
  ROOT = "my-apps",
  CREATE = "create",
  APP_STORE = "app-store",
  APP_ID = `:${MS_MY_APP_ID_KEY}`,
}

const S = MS_MY_APP_PATH_SEGMENTS;

// ─── Route Definition (inside Layout) ────────────────────────────────────────
export const myspaceMyAppRoutes: RouteObject = {
  path: S.ROOT,
  handle: { breadcrumb: "My Apps" },
  HydrateFallback: Spinner,
  children: [
    // The library sits under the tabbed layout (white panel + top tabs). Create
    // and the app runtime are standalone sub-flows — NOT children of the layout,
    // so they never render under the tab bar. Static "create" outranks ":id".
    {
      Component: MyAppMySpaceLayout,
      handle: { flushContent: true },
      children: [
        { index: true, Component: MyAppMySpaceLibrary },
        {
          path: S.APP_STORE,
          handle: { breadcrumb: "App Store" },
          Component: MyAppMySpaceAppStore,
        },
      ],
    },
    {
      path: S.CREATE,
      handle: { breadcrumb: "Create app" },
      Component: MyAppMySpaceCreate,
    },
    {
      path: S.APP_ID,
      handle: { breadcrumb: "App" },
      Component: MyAppMySpaceView,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const MS_MY_APP_PATHS = Object.freeze({
  /** The app library grid (module landing). */
  ROOT: addSlashes(S.ROOT),
  /** Describe + generate a new app. */
  CREATE: addSlashes(S.ROOT, S.CREATE),
  /** The app store catalog tab. */
  APP_STORE: addSlashes(S.ROOT, S.APP_STORE),
  /** Open a saved app by id (runs on open). */
  APP: (id: string | number) => addSlashes(S.ROOT, String(id)),
});
